import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Search, Filter, Receipt, Download, BellRing, CheckCircle, Clock, FileSpreadsheet, FileText } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import html2pdf from 'html2pdf.js';
import ReceiptModal from '../../UserDashboard/Maintenance/ReceiptModal';
import styles from './Maintenance.module.css';
import * as XLSX from 'xlsx';

const Maintenance = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [bills, setBills] = useState([]);
  const [stats, setStats] = useState({ totalExpected: 0, totalReceived: 0, totalOutstanding: 0 });
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBillForReceipt, setSelectedBillForReceipt] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [timeRange, setTimeRange] = useState('all');
  const pdfRef = React.useRef(null);

  const filteredBills = bills.filter(bill => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchSearch = bill.billNumber.toLowerCase().includes(searchLower) ||
                        (bill.resident && bill.resident.fullName.toLowerCase().includes(searchLower)) ||
                        (bill.resident && `${bill.resident.wing}-${bill.resident.flatNumber}`.toLowerCase().includes(searchLower));

    // Date filter
    let matchDate = true;
    if (timeRange !== 'all') {
      const billDate = new Date(bill.createdAt || bill.dueDate);
      const now = new Date();
      if (timeRange === 'weekly') {
        const lastWeek = new Date();
        lastWeek.setDate(now.getDate() - 7);
        matchDate = billDate >= lastWeek;
      } else if (timeRange === 'monthly') {
        const lastMonth = new Date();
        lastMonth.setMonth(now.getMonth() - 1);
        matchDate = billDate >= lastMonth;
      } else if (timeRange === '6months') {
        const last6Months = new Date();
        last6Months.setMonth(now.getMonth() - 6);
        matchDate = billDate >= last6Months;
      } else if (timeRange === 'yearly') {
        const lastYear = new Date();
        lastYear.setFullYear(now.getFullYear() - 1);
        matchDate = billDate >= lastYear;
      }
    }
    return matchSearch && matchDate;
  });

  const prepareExportData = () => {
    return filteredBills.map(bill => ({
      'Invoice ID': bill.billNumber,
      'Resident Name': bill.resident ? bill.resident.fullName : '-',
      'Unit': bill.resident ? `${bill.resident.wing}-${bill.resident.flatNumber}` : '-',
      'Billing Month': bill.billingMonth,
      'Base Amount (₹)': bill.baseAmount,
      'Additional Charges (₹)': bill.additionalCharges,
      'Late Fee (₹)': bill.lateFee,
      'Discount (₹)': bill.discount,
      'Total Amount (₹)': bill.totalAmount,
      'Status': bill.status,
      'Due Date': new Date(bill.dueDate).toLocaleDateString(),
      'Created At': new Date(bill.createdAt || Date.now()).toLocaleDateString()
    }));
  };

  const exportToPDF = () => {
    if (filteredBills.length === 0) return toast.error('No data to export');
    const element = pdfRef.current;
    const opt = {
      margin: 0.5,
      filename: `maintenance_report_${timeRange}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
    };
    html2pdf().set(opt).from(element).save();
  };

  const exportToExcel = () => {
    if (filteredBills.length === 0) return toast.error('No data to export');
    const data = prepareExportData();
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Maintenance Report');
    XLSX.writeFile(workbook, `maintenance_report_${timeRange}.xlsx`);
  };

  const [formData, setFormData] = useState({
    residentId: '', billingMonth: 'June 2025', baseAmount: '', additionalCharges: 0, lateFee: 0, discount: 0, dueDate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [billsRes, statsRes, residentsRes] = await Promise.all([
        api.get('/maintenance/all'),
        api.get('/maintenance/stats'),
        api.get('/admin/residents')
      ]);
      setBills(billsRes.data);
      setStats(statsRes.data);
      setResidents(residentsRes.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load maintenance data');
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/maintenance', formData);
      setBills([data, ...bills]);
      setIsDrawerOpen(false);
      setFormData({ residentId: '', billingMonth: 'June 2025', baseAmount: '', additionalCharges: 0, lateFee: 0, discount: 0, dueDate: '' });
      toast.success('Maintenance bill generated successfully!');
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate bill');
    }
  };

  const handleRemind = async (id) => {
    try {
      await api.post(`/maintenance/${id}/remind`);
      toast.success('Reminder sent!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reminder');
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Maintenance Billing" 
        breadcrumb={['Society Management', 'Maintenance']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Generate Bill
          </button>
        }
      />

      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryInfo}>
            <span>Total Expected</span>
            <h2>₹{stats.totalExpected.toLocaleString('en-IN')}</h2>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryInfo}>
            <span>Total Received</span>
            <h2 className={styles.textSuccess}>₹{stats.totalReceived.toLocaleString('en-IN')}</h2>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryInfo}>
            <span>Outstanding</span>
            <h2 className={styles.textDanger}>₹{stats.totalOutstanding.toLocaleString('en-IN')}</h2>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input 
              type="text" 
              placeholder="Search by Unit or Invoice ID..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className={styles.filterAndExport}>
            <select 
              className={styles.timeRangeSelect} 
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="6months">Last 6 Months</option>
              <option value="yearly">Yearly</option>
            </select>
            <button className={styles.exportBtnDoc} onClick={exportToPDF} title="Export to PDF Document">
              <FileText size={16} /> Document
            </button>
            <button className={styles.exportBtnExcel} onClick={exportToExcel} title="Export to Excel">
              <FileSpreadsheet size={16} /> Excel
            </button>
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Invoice ID</th>
                <th>Unit</th>
                <th>Resident</th>
                <th>Billing Month</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : filteredBills.length === 0 ? <tr><td colSpan="7" style={{textAlign: 'center', padding: '24px'}}>No records found</td></tr> : filteredBills.map(bill => (
                <tr key={bill._id}>
                  <td><strong>{bill.billNumber}</strong></td>
                  <td><strong>{bill.resident ? `${bill.resident.wing || ''}-${bill.resident.flatNumber || ''}` : '-'}</strong></td>
                  <td>{bill.resident ? bill.resident.fullName : '-'}</td>
                  <td>{bill.billingMonth}</td>
                  <td>₹{bill.totalAmount}</td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[bill.status.toLowerCase().replace(' ', '')]}`}>
                      {bill.status === 'Paid' ? <CheckCircle size={12} /> : <Clock size={12} />}
                      {bill.status}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionBtns}>
                      {bill.status !== 'Paid' && (
                        <button className={styles.btnRemind} title="Send Reminder" onClick={() => handleRemind(bill._id)}>
                          <BellRing size={14} />
                        </button>
                      )}
                      <button className={styles.btnDownload} title="Download Invoice" onClick={() => setSelectedBillForReceipt(bill)}>
                        <Download size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Generate Bill">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Select Resident</label>
            <select name="residentId" value={formData.residentId} onChange={handleChange} required>
              <option value="">-- Select Resident --</option>
              {residents.map(r => (
                <option key={r._id} value={r._id}>
                  {r.wing}-{r.flatNumber} | {r.fullName}
                </option>
              ))}
            </select>
          </div>
          <div className="formGroup">
            <label>Billing Month</label>
            <select name="billingMonth" value={formData.billingMonth} onChange={handleChange} required>
              <option value="May 2026">May 2026</option>
              <option value="June 2026">June 2026</option>
              <option value="July 2026">July 2026</option>
              <option value="August 2026">August 2026</option>
              <option value="September 2026">September 2026</option>
              <option value="October 2026">October 2026</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Base Amount (₹)</label>
            <input type="number" name="baseAmount" value={formData.baseAmount} onChange={handleChange} required placeholder="e.g. 3500" min="1" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="formGroup">
              <label>Additional Charges (₹)</label>
              <input type="number" name="additionalCharges" value={formData.additionalCharges} onChange={handleChange} min="0" />
            </div>
            <div className="formGroup">
              <label>Late Fee (₹)</label>
              <input type="number" name="lateFee" value={formData.lateFee} onChange={handleChange} min="0" />
            </div>
          </div>
          <div className="formGroup">
            <label>Discount (₹)</label>
            <input type="number" name="discount" value={formData.discount} onChange={handleChange} min="0" />
          </div>
          <div className="formGroup">
            <label>Due Date</label>
            <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} required />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Generate Invoice</button>
          </div>
        </form>
      </Drawer>

      <ReceiptModal 
        bill={selectedBillForReceipt} 
        onClose={() => setSelectedBillForReceipt(null)} 
      />

      {/* Hidden PDF template */}
      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', width: '1000px' }}>
        <div ref={pdfRef} style={{ padding: '20px', fontFamily: 'sans-serif', background: '#fff' }}>
          <h2 style={{ textAlign: 'center', color: '#173d2d', marginBottom: '20px' }}>Maintenance Report ({timeRange})</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left', backgroundColor: '#f3f4f6' }}>Invoice ID</th>
                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left', backgroundColor: '#f3f4f6' }}>Resident</th>
                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left', backgroundColor: '#f3f4f6' }}>Unit</th>
                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left', backgroundColor: '#f3f4f6' }}>Billing Month</th>
                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left', backgroundColor: '#f3f4f6' }}>Amount</th>
                <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left', backgroundColor: '#f3f4f6' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBills.map(bill => (
                <tr key={bill._id}>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{bill.billNumber}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{bill.resident ? bill.resident.fullName : '-'}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{bill.resident ? `${bill.resident.wing || ''}-${bill.resident.flatNumber || ''}` : '-'}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{bill.billingMonth}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>₹{bill.totalAmount}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{bill.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
